import {isFeatureEnabled} from 'feature-flags';

import {isNotNil} from 'ramda';

import {useGetRentalVehicleQuery} from '@dms/api/vehicle';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {testIds, vehiclesRoutes} from '@dms/routes';
import {Main, PageTab, useIsLicenceEnabled, usePermissions, usePricePermissions} from '@dms/shared';

import {buildArray, composePath, useRequiredParams} from 'shared/web';

import type {EagGridMaximization} from 'features/eag-grid';

import {VehicleEarningsAndCosts} from './components/VehicleEarningsAndCosts';
import {VehicleFinancingAndLeasing} from './components/VehicleFinancingAndLeasing/VehicleFinancingAndLeasing';
import {VehiclePrices} from './components/VehiclePrices';
import {VehicleRentalPrices} from './components/VehicleRentalPrices';

interface PricesAndFinancesProps {
  maximization?: EagGridMaximization;
}

export function PricesAndFinances(props: PricesAndFinancesProps) {
  const {id: vehicleId} = useRequiredParams();

  const {data: rentalVehicle} = useGetRentalVehicleQuery({vehicleId});
  const hasRentalVehicle = isNotNil(rentalVehicle);

  const {canViewAnyOfVehiclePriceFields, canReadVehicleCosts, canReadVehicleEarnings} =
    usePricePermissions({vehicleRecordId: vehicleId, businessCaseRecordId: null});

  // The panel itself refuses to render without a read permission; the tab is hidden here so a user
  // who can read neither product never sees an entry that leads to a permission message.
  const [canReadFinancing, canReadLeasing] = usePermissions({
    permissionKeys: ['saleVehicleFinancingRead', 'saleVehicleLeasingRead'],
  });

  const {isSalesModuleEnabled, isRentalVehicleModuleEnabled} = useIsLicenceEnabled();

  return (
    <Main
      data-testid={testIds.vehicles.finances('page')}
      tabs={buildArray<PageTab>()
        .when(canViewAnyOfVehiclePriceFields && isSalesModuleEnabled, {
          id: 'prices',
          title: i18n.t('entity.vehicle.labels.carPrices'),
          href: composePath(vehiclesRoutes.financesPrices, {params: {id: vehicleId}}),
          content: <VehiclePrices maximization={props.maximization} />,
        })
        .when(
          isFeatureEnabled(featureFlags.VEHICLE_FINANCING) &&
            isSalesModuleEnabled &&
            (canReadFinancing || canReadLeasing),
          {
            id: 'financingLeasing',
            title: i18n.t('page.vehicle.financingAndLeasing.title'),
            href: composePath(vehiclesRoutes.financesFinancingLeasing, {params: {id: vehicleId}}),
            content: <VehicleFinancingAndLeasing />,
          }
        )
        .when(canReadVehicleCosts || canReadVehicleEarnings, {
          id: 'earningsCosts',
          title: i18n.t('page.vehicle.labels.earningsCosts'),
          href: composePath(vehiclesRoutes.financesEarningsCosts, {params: {id: vehicleId}}),
          content: <VehicleEarningsAndCosts maximization={props.maximization} />,
        })
        .when(hasRentalVehicle && isRentalVehicleModuleEnabled, {
          id: 'rentalEarningsCosts',
          title: i18n.t('entity.rental.labels.rentalPrices'),
          href: composePath(vehiclesRoutes.financesRentalPrices, {params: {id: vehicleId}}),
          content: <VehicleRentalPrices maximization={props.maximization} />,
        })}
    />
  );
}
